import React, { useCallback, useMemo, useState } from "react";
import {
  Typography,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Box,
  CircularProgress,
  alpha,
} from "@mui/material";
import {
  HourglassTop as DemoRequestIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  PersonOutline as PersonIcon,
  Inventory2 as ProductIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import SchoolStatusTable from "../components/schools/SchoolStatusTable";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const STATUS_OPTIONS = ["Pending", "Approved", "Rejected"];

//Created By Cursor
const statusMeta = (status) => {
  const s = String(status || "").trim();
  const lower = s.toLowerCase();
  if (lower === "pending") {
    return {
      label: "Pending",
      color: crmColors.warning?.main || "#ea580c",
      soft: "#fff7ed",
    };
  }
  if (lower === "approved") {
    return {
      label: "Approved",
      color: crmColors.success?.main || "#16a34a",
      soft: "#f0fdf4",
    };
  }
  if (lower === "rejected") {
    return {
      label: "Rejected",
      color: crmColors.error?.main || "#dc2626",
      soft: "#fef2f2",
    };
  }
  return {
    label: s || "—",
    color: crmColors.grey?.[600] || "#64748b",
    soft: "#f8fafc",
  };
};

//Created By Cursor
const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

//Created By Cursor — My School > Demo Request
const DemoExtensionRequests = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [refreshKey, setRefreshKey] = useState(0);
  const [busyId, setBusyId] = useState("");

  const fetchRows = useCallback(
    () =>
      apiService.getDemoExtensionRequests({
        limit: 500,
        offset: 0,
        request_status: statusFilter,
      }),
    [statusFilter]
  );

  //Created By Cursor
  const handleStatusChange = useCallback(async (row, nextStatus, event) => {
    event?.stopPropagation?.();
    const requestId = String(row?.request_id || "").trim();
    const current = String(row?.request_status || "").trim();
    if (!requestId || !nextStatus || nextStatus === current || busyId) return;

    setBusyId(requestId);
    const result = await apiService.updateDemoExtensionRequestStatus(
      requestId,
      nextStatus
    );
    setBusyId("");
    if (result.success) {
      toast.success(result.message || `Status set to ${nextStatus}`);
      setRefreshKey((k) => k + 1);
    } else {
      toast.error(result.error || "Failed to update status");
    }
  }, [busyId]);

  //Created By Cursor
  const handleDelete = useCallback(async (row, event) => {
    event?.stopPropagation?.();
    const requestId = String(row?.request_id || "").trim();
    if (!requestId || busyId) return;
    if (!window.confirm("Delete this demo request?")) return;

    setBusyId(requestId);
    const result = await apiService.deleteDemoExtensionRequest(requestId);
    setBusyId("");
    if (result.success) {
      toast.success(result.message || "Request deleted");
      setRefreshKey((k) => k + 1);
    } else {
      toast.error(result.error || "Failed to delete request");
    }
  }, [busyId]);

  const banner = useMemo(
    () => (
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "#fff",
          boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack spacing={0.25}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Filter by status
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Newest requests first. Change status from the Status column.
            </Typography>
          </Stack>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={statusFilter}
            onChange={(_, next) => {
              if (next) setStatusFilter(next);
            }}
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                px: 1.6,
                fontWeight: 700,
                borderRadius: "10px !important",
                borderColor: "divider",
              },
              "& .Mui-selected": {
                bgcolor: `${crmColors.primary?.main || "#0288d1"} !important`,
                color: "#fff !important",
              },
            }}
          >
            <ToggleButton value="ALL">All</ToggleButton>
            <ToggleButton value="Pending">Pending</ToggleButton>
            <ToggleButton value="Approved">Approved</ToggleButton>
            <ToggleButton value="Rejected">Rejected</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>
    ),
    [statusFilter]
  );

  return (
    <SchoolStatusTable
      title="Demo Request"
      subtitle="Demo extension requests from schools on the Expired page"
      breadcrumbLabel="Demo Request"
      breadcrumbIcon={<DemoRequestIcon sx={{ color: "#ffffff", fontSize: 18 }} />}
      fetchRows={fetchRows}
      banner={banner}
      itemLabel="request"
      searchPlaceholder="Search by school, code, contact…"
      showSerial
      polished
      refreshKey={refreshKey}
      getRowKey={(row) => row.request_id || `${row.school_code}-${row.date_added}`}
      getRowSx={(row) => {
        const meta = statusMeta(row.request_status);
        return {
          bgcolor: alpha(meta.color, 0.03),
          "&:hover": { bgcolor: alpha(meta.color, 0.07) },
        };
      }}
      onRowClick={(row) => {
        if (row?.school_code) {
          navigate(`/schools/detail/${encodeURIComponent(row.school_code)}`);
        }
      }}
      searchFields={(row) => [
        row.school_name,
        row.school_code,
        row.admin_name,
        row.admin_phone,
        row.request_status,
        row.requested_by_name,
        row.product_code,
      ]}
      columns={[
        {
          id: "date",
          label: "Date",
          render: (row) => (
            <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              {formatDateTime(row.date_added)}
            </Typography>
          ),
        },
        {
          id: "name",
          label: "School",
          render: (row) => (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box
                sx={{
                  mt: 0.15,
                  width: 30,
                  height: 30,
                  borderRadius: 1.5,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(crmColors.primary?.main || "#0288d1", 0.1),
                  color: crmColors.primary?.main || "#0288d1",
                  flexShrink: 0,
                }}
              >
                <SchoolIcon sx={{ fontSize: 16 }} />
              </Box>
              <Stack spacing={0.15}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {row.school_name || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {row.school_code || "—"}
                </Typography>
              </Stack>
            </Stack>
          ),
        },
        {
          id: "contact",
          label: "Contact",
          render: (row) => (
            <Stack spacing={0.15}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {row.admin_name || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.admin_phone || "—"}
              </Typography>
            </Stack>
          ),
        },
        {
          id: "requested_by",
          label: "Requested By",
          render: (row) => (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Stack spacing={0.1}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {row.requested_by_name || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.requested_by || "—"}
                </Typography>
              </Stack>
            </Stack>
          ),
        },
        {
          id: "product",
          label: "Product",
          render: (row) => (
            <Chip
              size="small"
              icon={<ProductIcon sx={{ fontSize: "14px !important" }} />}
              label={row.product_code || "—"}
              sx={{
                height: 26,
                fontWeight: 800,
                borderRadius: "8px",
                bgcolor: alpha(crmColors.primary?.main || "#0288d1", 0.08),
                color: crmColors.primary?.dark || "#01579b",
                "& .MuiChip-icon": { color: crmColors.primary?.main || "#0288d1" },
              }}
            />
          ),
        },
        {
          id: "status",
          label: "Status",
          render: (row) => {
            const meta = statusMeta(row.request_status);
            const requestId = String(row.request_id || "");
            const isBusy = busyId === requestId;
            return (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                onClick={(e) => e.stopPropagation()}
              >
                <FormControl size="small" sx={{ minWidth: 128 }}>
                  <Select
                    value={STATUS_OPTIONS.includes(row.request_status) ? row.request_status : "Pending"}
                    disabled={isBusy}
                    onChange={(e) => handleStatusChange(row, e.target.value, e)}
                    renderValue={(value) => {
                      const m = statusMeta(value);
                      return (
                        <Chip
                          size="small"
                          label={m.label}
                          sx={{
                            height: 24,
                            fontWeight: 800,
                            color: "#fff",
                            bgcolor: m.color,
                            borderRadius: "8px",
                          }}
                        />
                      );
                    }}
                    sx={{
                      borderRadius: 2,
                      bgcolor: meta.soft,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: alpha(meta.color, 0.35),
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: meta.color,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: meta.color,
                      },
                    }}
                  >
                    {STATUS_OPTIONS.map((opt) => {
                      const m = statusMeta(opt);
                      return (
                        <MenuItem key={opt} value={opt}>
                          <Chip
                            size="small"
                            label={m.label}
                            sx={{
                              height: 22,
                              fontWeight: 800,
                              color: "#fff",
                              bgcolor: m.color,
                              borderRadius: "8px",
                            }}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {isBusy ? <CircularProgress size={16} /> : null}
              </Stack>
            );
          },
        },
        {
          id: "actions",
          label: "Action",
          align: "center",
          render: (row) => (
            <Tooltip title="Delete request">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={busyId === String(row.request_id || "")}
                  onClick={(e) => handleDelete(row, e)}
                  sx={{
                    borderRadius: 2,
                    bgcolor: alpha(crmColors.error?.main || "#dc2626", 0.08),
                    "&:hover": {
                      bgcolor: alpha(crmColors.error?.main || "#dc2626", 0.16),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ),
        },
      ]}
    />
  );
};

export default DemoExtensionRequests;
